import React, { useRef, useEffect } from "react";
import firstScreenVideo from "../../assets/videos/banner.mp4";
import { Link, useNavigate } from "react-router-dom";
import clsx from "clsx";
import AnimatedContent from "../AnimatedContent";
import { useJoinWaitlistModal } from "../../context/ModalContext";

const FirstScreen = () => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const { openJoinWaitlistModal } = useJoinWaitlistModal();
    const navigate = useNavigate();
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        let isVideoReady = false;
        let lastScrollTop = 0;
        const scrollDistance = 500;

        const handleLoadedMetadata = () => {
            video.currentTime = 0;
            video.pause();
            isVideoReady = true;
        };

        const handleScroll = () => {
            if (!isVideoReady || !video.duration || isNaN(video.duration)) return;

            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

            if (scrollTop === lastScrollTop) return;

            lastScrollTop = scrollTop;

            const progress = Math.max(0, Math.min(1, scrollTop / scrollDistance));
            const targetTime = progress * video.duration;

            if (scrollTop >= scrollDistance) {
                video.currentTime = video.duration;
            } else {
                if (Math.abs(video.currentTime - targetTime) > 0.1) {
                    video.currentTime = targetTime;
                }
            }
        };

        video.addEventListener('loadedmetadata', handleLoadedMetadata);
        video.addEventListener('error', (e) => console.error('Video error:', e));

        let ticking = false;
        const throttledScroll = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    handleScroll();
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener('scroll', throttledScroll, { passive: true });

        if (video.readyState >= 1) {
            handleLoadedMetadata();
        }

        return () => {
            window.removeEventListener('scroll', throttledScroll);
            video.removeEventListener('loadedmetadata', handleLoadedMetadata);
            video.removeEventListener('error', () => { });
        };
    }, []);

    return <div className={clsx(
        "h-[calc(100vh-86px)] relative px-[16px] flex flex-col items-center gap-[50px]",
        "md:gap-[64px]"
    )}>
        <div className={clsx(
            "absolute top-[-11%] left-[50%] translate-x-[-50%] w-full h-[80%] max-w-[1080px]",
            "md:top-0"
        )}>
            <video
                ref={videoRef}
                src={firstScreenVideo}
                muted
                preload="metadata"
                controls={false}
                className="w-full max-w-[1200px] h-full md:object-contain object-cover pointer-events-none"
            />
        </div>
        <div className={clsx(
            "w-full flex-1 flex flex-col items-center justify-center relative z-[2] pt-[90px]",
        )}>
            <AnimatedContent>
                <div className={clsx(
                    "text-center text-[32px] font-semibold leading-[1.2] tracking-[-1.84px] ",
                    "md:text-[82px]"
                )}>
                    Put your TAO to work.
                </div>
            </AnimatedContent>
            <AnimatedContent>
                <div className="flex flex-col items-center">
                    <div className="text-center md:text-[82px] text-[32px] font-semibold leading-[1.2] tracking-[-1.84px] md:mb-[16px] mb-[20px]">
                        Earn yield. Trade with power.
                    </div>
                    <div className="text-white/50 text-center md:text-[18px] text-[14px] mb-[37px]">
                        Earn stable returns or 10× your subnet exposure — all in one seamless protocol built for TAO.
                    </div>

                    <div className="flex md:flex-row flex-col gap-[12px] w-full justify-center items-center">
                        <div className="w-full md:w-fit flex justify-center" onClick={() => openJoinWaitlistModal(() => {
                            navigate('/dashboard');
                        })}>
                            <button className="px-[40px] py-[24px] md:w-fit w-full max-w-[263px] rounded-[100px] bg-white text-black text-[16px] font-medium border border-transparent hover:opacity-80 transition-all duration-300">
                                Launch App
                            </button>
                        </div>
                        <Link to="https://taoflow-1.gitbook.io/taoflow-docs" className="w-full md:w-fit flex justify-center">
                            <button className="px-[40px] py-[24px] md:w-fit w-full max-w-[263px] rounded-[100px] border-btn text-[16px] font-medium transition-all duration-300">
                                How It Works →
                            </button>
                        </Link>
                    </div>
                </div>
            </AnimatedContent>
        </div>
        <div className="pb-[40px] md:pb-[80px]">
            <div className={clsx(
                "w-[16px] h-[26px] rounded-[100px] border-[2px] border-white flex items-center justify-center",
            )}>
                <div className="h-[5px] w-[2px] animate-arrow-float bg-white" />
            </div>
        </div>
    </div>;
};

export default FirstScreen;
